import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FiBookOpen, FiUsers, FiCheckSquare, FiTrendingUp, FiChevronRight, FiBell, FiArrowUpRight } from 'react-icons/fi';
import useInstructorDashboard from './useInstructorDashboard';
import InstructorActiveAttendanceSessions from './InstructorActiveAttendanceSessions';
import SessionQnaPanel from '../sessionQna/SessionQnaPanel';
import { extractWeekNumber } from './activeAttendanceSessionsHelpers';
import styles from './InstructorDashboard.module.css';

function InstructorDashboard() {
  const {
    user, navigate, loading, stats, taughtCourses, showInactiveCourses, setShowInactiveCourses,
    pendingExtensions, handleCourseClick, quickActions, getStatIconClass, filteredCourses
  } = useInstructorDashboard();
  const [qnaSession, setQnaSession] = useState(null);

  // Reveal the whole dashboard in one step instead of the live-sessions card popping in on its own
  // and pushing everything down. The safety timeout keeps a slow request from hiding it forever.
  const [sessionsReady, setSessionsReady] = useState(false);
  const [revealTimedOut, setRevealTimedOut] = useState(false);
  const onSessionsReady = useCallback(() => setSessionsReady(true), []);
  useEffect(() => {
    const timer = setTimeout(() => setRevealTimedOut(true), 4000);
    return () => clearTimeout(timer);
  }, []);
  const dashboardReady = revealTimedOut || (!loading && sessionsReady);

  return (
    <div className={styles.container}>
      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerTitle}>
            <span className={styles.greetingArabic} dir="rtl">السلام عليكم</span>
            <span className={styles.greetingName}>, {user?.first_name || 'Instructor'}</span>
          </div>
          <p className={styles.headerSubtitle}>Manage your courses and track student progress.</p>
        </div>
        <div className={styles.headerAction}>
          <button
            className={styles.manageCoursesButton}
            onClick={() => navigate('/admin/courses')}
          >
            Manage All Courses
          </button>
        </div>
      </div>
      <div className={styles.headerDivider} />

      {/* Everything below the header is revealed together (display: contents keeps the layout once
          shown; display: none still lets the sections mount and fetch). */}
      <div style={{ display: dashboardReady ? 'contents' : 'none' }}>

      {/* My Live Sessions - mirrors the student dashboard's active-sessions card */}
      <InstructorActiveAttendanceSessions onOpenQna={setQnaSession} onReady={onSessionsReady} />

      {/* Session Q&A - its own independent section, not nested in the
          Live Sessions card, opened via that card's Session Q&A button */}
      <SessionQnaPanel
        open={Boolean(qnaSession)}
        onClose={() => setQnaSession(null)}
        sessionId={qnaSession?.session_id}
        role="instructor"
        title={qnaSession ? `Session Q&A · ${extractWeekNumber(qnaSession.title) ? `Week ${extractWeekNumber(qnaSession.title)}` : (qnaSession.course_code || qnaSession.elective_code || qnaSession.title || '')}` : ''}
      />

      {/* Pending Extensions Banner */}
      {pendingExtensions > 0 && (
        <button
          type="button"
          className={styles.extensionsBanner}
          onClick={() => navigate('/admin/extensions')}
        >
          <span className={styles.extensionsBannerIcon}>
            <FiBell size={18} />
          </span>
          <span className={styles.extensionsBannerBody}>
            <span className={styles.extensionsBannerTitle}>
              {pendingExtensions} pending extension {pendingExtensions === 1 ? 'request' : 'requests'}
            </span>
            <span className={styles.extensionsBannerCaption}>
              Review and respond on the Extensions page.
            </span>
          </span>
          <span className={styles.extensionsBannerArrow}>
            <FiArrowUpRight size={16} />
          </span>
        </button>
      )}

      {/* Stats Grid -- gated on !loading, not just rendered with the default
          zeroed state: `stats` starts at real 0s (not null), so there was no
          way to tell "not fetched yet" from "genuinely zero" and every
          instructor saw 0 in all four tiles before the real numbers arrived. */}
      {!loading && (
      <div className={styles.statsGrid}>
        <motion.div
          className={styles.statCard}
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className={`${styles.statIcon} ${getStatIconClass('primary')}`}>
            <FiBookOpen size={20} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{Number(stats.coursesTaught) || 0}</div>
            <div className={styles.statLabel}>COURSES TAUGHT</div>
          </div>
        </motion.div>

        <motion.div
          className={styles.statCard}
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <div className={`${styles.statIcon} ${getStatIconClass('success')}`}>
            <FiUsers size={20} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{Number(stats.totalStudents) || 0}</div>
            <div className={styles.statLabel}>TOTAL STUDENTS</div>
          </div>
        </motion.div>

        <motion.div
          className={styles.statCard}
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <div className={`${styles.statIcon} ${getStatIconClass('warning')}`}>
            <FiCheckSquare size={20} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{Number(stats.attendanceSessions) || 0}</div>
            <div className={styles.statLabel}>ATTENDANCE SESSIONS</div>
          </div>
        </motion.div>

        <motion.div
          className={styles.statCard}
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <div className={`${styles.statIcon} ${getStatIconClass('accent')}`}>
            <FiTrendingUp size={20} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{Number(stats.averageAttendance) || 0}%</div>
            <div className={styles.statLabel}>AVG ATTENDANCE</div>
          </div>
        </motion.div>
      </div>
      )}

      {/* Main Content Grid */}
      <div className={styles.contentGrid}>
        {/* My Courses Section */}
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>My Courses</h2>
            {taughtCourses.some(course => !(course.is_active === 1 || course.is_active === true)) && (
              <button
                type="button"
                className={styles.toggleInactiveButton}
                onClick={() => setShowInactiveCourses(!showInactiveCourses)}
              >
                {showInactiveCourses ? 'Hide' : 'Show'} Previous Courses
              </button>
            )}
          </div>
          <div className={styles.coursesList}>
            {/* Gated on !loading: taughtCourses starts at [], so rendering
                unconditionally claimed "No courses assigned yet." to every
                instructor for the length of the fetch. */}
            {loading ? null : filteredCourses.length > 0 ? (
              filteredCourses.map((course) => (
                <motion.div
                  key={course.id}
                  className={styles.courseRow}
                  initial={false}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className={styles.courseInfo}>
                    <div className={styles.courseTitleRow}>
                      <span className={styles.courseCode}>{course.code}</span>
                      <div className={styles.courseName}>{course.name}</div>
                    </div>
                    <div className={styles.courseStats}>
                      <span className={styles.courseStat}>
                        {course.enrolled_students || 0} Students
                      </span>
                      <span className={styles.courseStat}>
                        {course.session_count || 0} Sessions
                      </span>
                    </div>
                    <button
                      className={styles.courseManageButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCourseClick(course.id);
                      }}
                    >
                      <FiBookOpen size={14} />
                      Manage Course
                    </button>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className={styles.emptyState}>
                <FiBookOpen />
                <p>
                  {taughtCourses.length > 0
                    ? showInactiveCourses
                      ? 'No courses found.'
                      : 'No active courses found.'
                    : 'No courses assigned yet.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions Section */}
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Quick Actions</h2>
          </div>
          <div className={styles.quickActions}>
            <div className={styles.actionGrid}>
              {quickActions.map((action, index) => {
                const IconComponent = action.icon;
                return (
                  <motion.div
                    key={action.id}
                    className={styles.actionItem}
                    initial={false}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0 }}
                    onClick={action.onClick}
                  >
                    <div className={styles.actionIcon}>
                      <IconComponent size={18} />
                    </div>
                    <div className={styles.actionContent}>
                      <div className={styles.actionTitle}>{action.title}</div>
                      <div className={styles.actionDescription}>{action.description}</div>
                    </div>
                    <FiChevronRight className={styles.actionChevron} size={18} />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default InstructorDashboard;
